import {Trans} from '@lingui/macro';
import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Constants.
 */

const defaultIconSize = 16;
const defaultBorderRadius = 8;

/*
 * Props.
 */

interface IconBoxProps {
  iconName: IconName;
  iconSize?: number;
  iconColor?: string;
  backgroundColor?: string;
  className?: string;
  borderRadius?: number;

  onRequestClose?: Handler;
}

/*
 * Style.
 */

interface StyledIconBoxDivProps {
  $backgroundColor: string | undefined;
  $borderRadius: number | undefined;
}

const StyledIconBoxDiv = styled.div<StyledIconBoxDivProps>`
  position: relative;
  background-color: ${(p) => p.$backgroundColor || p.theme.palette.blue.shade20};
  border-radius: ${(p) => p.$borderRadius || defaultBorderRadius}px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px;
`;

const StyledCloseButtonWrapperDiv = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
`;

/*
 * Component.
 */

export const IconBox: FC<PropsWithChildren<IconBoxProps>> = (props) => {
  const {iconName, iconSize, iconColor, backgroundColor, className, borderRadius, children, onRequestClose} =
    props;
  const theme = useUserTheme();
  const size = iconSize || defaultIconSize;
  const color = iconColor || theme.greys.shade80;
  return (
    <StyledIconBoxDiv $backgroundColor={backgroundColor} $borderRadius={borderRadius} className={className}>
      <Icon name={iconName} size={size} color={color} />
      {children}
      {onRequestClose && (
        <StyledCloseButtonWrapperDiv>
          <IconButton
            iconName="closeSmall"
            onClick={onRequestClose}
            label={<Trans id="common-icon-box-close">Close</Trans>}
          />
        </StyledCloseButtonWrapperDiv>
      )}
    </StyledIconBoxDiv>
  );
};
